import { createClient } from '@/lib/supabase/client'
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { logger } from '@/lib/logger'
import { fetchAllPages } from '@/lib/supabase/fetchAllPages'
import { parseRoles } from '@/utils/roleHelper'
import { removePurchaseFromMemory, removeItemFromMemory, findPurchaseInMemory, updatePurchaseInMemory, notifyCacheListeners } from '@/stores/purchaseMemoryStore'

export interface SupportAttachment {
  url: string
  name: string
  size: number
  path: string
}

interface SupportStatementRow {
  id: string
  statement_code?: string
  vendor_name?: string
  statement_date?: string
  uploaded_at: string
  file_name?: string
  total_amount?: number
  grand_total?: number
  status: string
}

export type SupportInquiryType =
  | 'bug'
  | 'modify'
  | 'delete'
  | 'other'
  | 'annual_leave'
  | 'attendance'
  | 'delivery_date_change'
  | 'quantity_change'
  | 'price_change'
  | 'item_add'
  | 'new_vendor'
  | 'vendor_edit'
  | 'shipping_edit'

// 업체등록 요청(new_vendor) payload
export interface NewVendorInquiryPayload {
  vendor: {
    vendor_name: string
    vendor_alias?: string
    vendor_phone?: string
    vendor_fax?: string
    vendor_payment_schedule?: string
    vendor_address?: string
    note?: string
  }
  contacts: {
    contact_name: string
    position?: string
    contact_phone?: string
    contact_email?: string
  }[]
}

// 기존업체 수정 요청(vendor_edit) payload: 수정 대상 업체 id + 수정된 전체 내용
export interface VendorEditInquiryPayload {
  vendor_id: number
  vendor: {
    vendor_name: string
    vendor_alias?: string
    vendor_phone?: string
    vendor_fax?: string
    vendor_payment_schedule?: string
    vendor_address?: string
    note?: string
  }
  contacts: {
    contact_name: string
    position?: string
    contact_phone?: string
    contact_email?: string
  }[]
}

// 택배 주소록 수정 요청(shipping_edit) payload: 수정 대상 주소록(shipping_contacts) id + 수정된 전체 내용
export interface ShippingEditInquiryPayload {
  address_id: string
  shipping: {
    company_name: string
    contact_name_only: string
    contact_title?: string
    contact_memo?: string
    phone?: string
    mobile?: string
    email?: string
    address?: string
  }
}

export type SupportInquiryPayload =
  | {
      requested_date: string
      current_date?: string | null
    }
  | {
      items: {
        item_id: string
        line_number?: number | null
        item_name: string
        specification?: string | null
        current_quantity?: number | null
        new_quantity: number
      }[]
    }
  | {
      items: {
        item_id: string
        line_number?: number | null
        item_name: string
        specification?: string | null
        change_type?: 'unit_price' | 'amount'
        current_unit_price?: number | null
        new_unit_price?: number | null
        current_amount?: number | null
        new_amount?: number | null
      }[]
    }
  | {
      reason?: string
      delete_target?: 'purchase' | 'statement'
      delete_type?: 'all' | 'items'
      delete_mode?: 'hard' | 'soft'
      delete_items?: {
        item_id: string
        line_number?: number | null
        item_name: string
        specification?: string | null
      }[]
      statement_id?: string
      statement_info?: string
    }
  | {
      items: {
        item_name: string
        specification?: string | null
        quantity: number
        unit: string
        unit_price: number
        remark?: string | null
      }[]
    }
  | NewVendorInquiryPayload
  | VendorEditInquiryPayload
  | ShippingEditInquiryPayload

export interface SupportInquiry {
  id?: number
  created_at?: string
  updated_at?: string
  user_id?: string
  user_email?: string
  user_name?: string
  inquiry_type: SupportInquiryType
  subject: string
  message: string
  status?: 'open' | 'in_progress' | 'resolved' | 'closed'
  handled_by?: string
  resolution_note?: string
  purchase_request_id?: number | null
  purchase_info?: string
  purchase_order_number?: string
  processed_at?: string
  requester_id?: string
  purchase_requests?: { id: number; purchase_order_number?: string } | null
  attachments?: SupportAttachment[]
  inquiry_payload?: SupportInquiryPayload | null
}

export interface SupportInquiryMessage {
  id: number
  inquiry_id: number
  sender_role: 'user' | 'admin' | 'system'
  sender_email: string
  message: string
  attachments: SupportAttachment[]
  created_at: string
}

export interface CreateSupportInquiryPayload {
  inquiry_type: SupportInquiryType
  subject: string
  message: string
  purchase_request_id?: number
  purchase_info?: string
  purchase_order_number?: string
  attachments?: SupportAttachment[]
  inquiry_payload?: SupportInquiryPayload | null
}

class SupportService {
  private supabase = createClient()
  private readonly BUCKET_NAME = 'support-attachments'
  private inquiriesChannel: RealtimeChannel | null = null
  private inquirySubscribers = new Set<(payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void>()

  // 이미지 첨부파일 업로드
  async uploadAttachment(file: File): Promise<{ success: boolean; data?: SupportAttachment; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, error: '로그인이 필요합니다.' }

      // 파일 확장자 추출
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      const timestamp = Date.now()
      const randomStr = Math.random().toString(36).substring(2, 8)
      const fileName = `support_${timestamp}_${randomStr}.${fileExt}`
      const filePath = `inquiries/${user.id}/${fileName}`

      // Storage에 업로드
      const { error: uploadError } = await this.supabase.storage
        .from(this.BUCKET_NAME)
        .upload(filePath, file, {
          contentType: file.type,
          upsert: false,
        })

      if (uploadError) {
        logger.error('첨부파일 업로드 에러', uploadError)
        throw uploadError
      }

      // Public URL 생성
      const { data: { publicUrl } } = this.supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(filePath)

      return {
        success: true,
        data: {
          url: publicUrl,
          name: file.name,
          size: file.size,
          path: filePath
        }
      }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '파일 업로드 실패' }
    }
  }

  // 첨부파일 삭제
  async deleteAttachment(path: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase.storage
        .from(this.BUCKET_NAME)
        .remove([path])

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '파일 삭제 실패' }
    }
  }

  // 문의 정렬: 완료되지 않은 문의를 먼저, 그 안에서 최신순
  private sortInquiriesByStatus(inquiries: SupportInquiry[]): SupportInquiry[] {
    return inquiries.sort((a, b) => {
      // 완료 상태: resolved, closed
      const aCompleted = a.status === 'resolved' || a.status === 'closed'
      const bCompleted = b.status === 'resolved' || b.status === 'closed'
      
      // 완료되지 않은 문의를 먼저
      if (aCompleted !== bCompleted) {
        return aCompleted ? 1 : -1
      }
      
      // 같은 그룹 내에서는 최신순 (created_at 내림차순)
      const aDate = new Date(a.created_at || 0).getTime()
      const bDate = new Date(b.created_at || 0).getTime()
      return bDate - aDate
    })
  }

  // 문의 생성
  async createInquiry(payload: CreateSupportInquiryPayload): Promise<{ success: boolean; inquiryId?: number; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, error: '로그인이 필요합니다.' }

      const { data: employee } = await this.supabase
        .from('employees')
        .select('id, name, email')
        .eq('email', user.email)
        .maybeSingle()

      const { data: created, error } = await this.supabase
        .from('support_inquires')
        .insert({
          user_id: user.id,
          user_email: employee?.email || user.email,
          user_name: employee?.name || '',
          requester_id: employee?.id,
          inquiry_type: payload.inquiry_type,
          subject: payload.subject,
          message: payload.message,
          purchase_request_id: payload.purchase_request_id ?? null,
          purchase_info: payload.purchase_info,
          purchase_order_number: payload.purchase_order_number,
          inquiry_payload: payload.inquiry_payload ?? null,
          status: 'open',
          attachments: payload.attachments || []
        })
        .select('id')
        .single()

      if (error) {
        logger.error('문의 등록 에러', error, {
          code: error.code,
          details: error.details,
          hint: error.hint
        })
        return { success: false, error: error.message }
      }

      const inquiryId = created?.id as number | undefined
      if (!inquiryId) {
        return { success: false, error: '문의 ID를 확인할 수 없습니다.' }
      }

      // ✅ 입고일 변경 요청은 진입 경로와 무관하게 지연 알림 차단 플래그를 즉시 세움
      // (플래그가 없으면 처리 완료 전까지 홈의 입고 지연 알림 모달이 계속 뜸)
      if (payload.inquiry_type === 'delivery_date_change' && payload.purchase_request_id) {
        await this.markDeliveryRevisionRequested(
          payload.purchase_request_id,
          employee?.name || user.email || 'unknown'
        )
      }

      return { success: true, inquiryId }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '문의 접수 실패' }
    }
  }

  // 입고일 변경 요청 접수 시 지연 알림 차단 플래그 설정 (실패해도 문의 접수 자체는 유지)
  private async markDeliveryRevisionRequested(purchaseRequestId: number, byName: string): Promise<void> {
    try {
      const nowIso = new Date().toISOString()
      const { error } = await this.supabase
        .from('purchase_requests')
        .update({
          delivery_revision_requested: true,
          delivery_revision_requested_at: nowIso,
          delivery_revision_requested_by: byName
        })
        .eq('id', purchaseRequestId)

      if (error) {
        logger.error('입고일 변경 요청 플래그 업데이트 실패', error)
        return
      }

      const updated = updatePurchaseInMemory(purchaseRequestId, (p) => ({
        ...p,
        delivery_revision_requested: true,
        delivery_revision_requested_at: nowIso,
        delivery_revision_requested_by: byName
      }))
      if (updated) notifyCacheListeners()
    } catch (e) {
      logger.error('입고일 변경 요청 플래그 업데이트 실패', e)
    }
  }

  async getInquiryMessages(inquiryId: number): Promise<{ success: boolean; data: SupportInquiryMessage[]; error?: string }> {
    try {
      const { data, error } = await this.supabase
        .from('support_inquiry_messages')
        .select('*')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return { success: true, data: (data || []) as SupportInquiryMessage[] }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '메시지 조회 실패' }
    }
  }

  async sendInquiryMessage(params: {
    inquiryId: number
    message: string
    attachments?: SupportAttachment[]
    senderRole: 'user' | 'admin'
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, error: '로그인이 필요합니다.' }

      const senderEmail = user.email || ''
      if (!senderEmail) return { success: false, error: '사용자 이메일을 확인할 수 없습니다.' }

      const { error } = await this.supabase
        .from('support_inquiry_messages')
        .insert({
          inquiry_id: params.inquiryId,
          sender_role: params.senderRole,
          sender_email: senderEmail,
          message: params.message,
          attachments: params.attachments || []
        })

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '메시지 전송 실패' }
    }
  }

  subscribeToInquiryMessages(inquiryId: number, callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void) {
    return this.supabase
      .channel(`support_inquiry_messages_${inquiryId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_inquiry_messages',
          filter: `inquiry_id=eq.${inquiryId}`
        },
        callback
      )
      .subscribe()
  }

  async resolveInquiry(inquiryId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: inquiryMeta } = await this.supabase
        .from('support_inquires')
        .select('inquiry_type,inquiry_payload,purchase_request_id')
        .eq('id', inquiryId)
        .maybeSingle()
      const meta = inquiryMeta as { inquiry_type?: string; purchase_request_id?: number | string | null; inquiry_payload?: { items?: { item_id?: string }[]; delete_target?: string; delete_type?: string; delete_mode?: string; delete_items?: { item_id?: string | number }[]; statement_id?: string } } | null
      const payloadItems = Array.isArray(meta?.inquiry_payload?.items)
        ? meta.inquiry_payload.items
        : []
      const hasUndefinedItemId = payloadItems.some((item: { item_id?: string }) => {
        const rawItemId = item?.item_id
        return rawItemId === undefined || rawItemId === null || rawItemId === 'undefined' || rawItemId === ''
      })
      const inquiryType = meta?.inquiry_type || null
      const shouldUseFallbackResolve =
        (inquiryType === 'quantity_change' || inquiryType === 'price_change') && hasUndefinedItemId

      if (shouldUseFallbackResolve) {
        const fallbackResult = await this.updateInquiryStatus(inquiryId, 'resolved')
        if (!fallbackResult.success) {
          return { success: false, error: fallbackResult.error || '완료 처리 실패' }
        }
        return { success: true }
      }

      // 거래명세서 삭제 요청인 경우: RPC 호출 전에 image_url 조회 (RPC에서 DB 삭제 후 조회 불가)
      let statementImageUrl: string | null = null
      if (inquiryType === 'delete' && meta?.inquiry_payload?.delete_target === 'statement' && meta?.inquiry_payload?.statement_id) {
        const { data: stmt } = await this.supabase
          .from('transaction_statements')
          .select('image_url')
          .eq('id', meta.inquiry_payload.statement_id)
          .maybeSingle()
        statementImageUrl = (stmt as { image_url?: string } | null)?.image_url || null
      }

      const { error } = await this.supabase.rpc('resolve_inquiry', { p_inquiry_id: inquiryId })
      if (error) throw error

      // 삭제 완료 시 메모리 캐시 정리 (resolve는 서버 RPC라 클라 메모리 캐시가 자동 갱신되지 않음 → 발주가 화면에 남는 문제 방지)
      if (inquiryType === 'delete' && meta?.inquiry_payload?.delete_target !== 'statement' && meta?.purchase_request_id != null) {
        const prid = meta.purchase_request_id
        if (meta?.inquiry_payload?.delete_type === 'items') {
          // 품목별 삭제: 삭제된 품목만 캐시에서 제거
          const delItems = Array.isArray(meta?.inquiry_payload?.delete_items) ? meta.inquiry_payload.delete_items : []
          // hard 모드 번호 당김 반영을 위해 제거 전에 삭제 대상 품목의 line_number 수집
          const delIds = new Set(delItems.filter(it => it?.item_id != null).map(it => String(it.item_id)))
          const cachedPurchase = findPurchaseInMemory(prid)
          const cachedItems = cachedPurchase
            ? ((cachedPurchase.items && cachedPurchase.items.length > 0) ? cachedPurchase.items : (cachedPurchase.purchase_request_items || []))
            : []
          const deletedLines = cachedItems
            .filter(it => delIds.has(String(it.id)))
            .map(it => it.line_number)
            .filter((n): n is number => n != null)
          delItems.forEach((it) => {
            if (it?.item_id != null) removeItemFromMemory(prid, it.item_id)
          })
          // hard 모드: 서버(RPC)가 당긴 line_number를 캐시에도 동일 규칙으로 반영
          // (삭제된 라인보다 뒤의 품목만 삭제 라인 수만큼 감소, soft 모드는 번호 유지)
          if (meta?.inquiry_payload?.delete_mode === 'hard' && deletedLines.length > 0) {
            updatePurchaseInMemory(prid, (purchase) => {
              const items = (purchase.items && purchase.items.length > 0) ? purchase.items : (purchase.purchase_request_items || [])
              const shifted = items.map(it =>
                it.line_number != null
                  ? { ...it, line_number: it.line_number - deletedLines.filter(dl => dl < (it.line_number as number)).length }
                  : it
              )
              return {
                ...purchase,
                items: purchase.items ? shifted : purchase.items,
                purchase_request_items: purchase.purchase_request_items ? shifted : purchase.purchase_request_items,
              }
            })
          }
        } else {
          // 발주 전체 삭제: 발주를 캐시에서 즉시 제거
          removePurchaseFromMemory(prid)
        }
      }

      // 거래명세서 삭제 완료 후 Storage 파일 삭제
      if (statementImageUrl) {
        try {
          const path = statementImageUrl.split('/receipt-images/')[1]
          if (path) {
            await this.supabase.storage.from('receipt-images').remove([path])
          }
        } catch (e) {
          logger.warn('거래명세서 Storage 파일 삭제 실패 (DB 삭제는 완료):', { error: e })
        }
      }

      // 상태 확인(비밀 없음): resolved로 실제 반영됐는지
      await this.supabase
        .from('support_inquires')
        .select('id,status,processed_at,updated_at')
        .eq('id', inquiryId)
        .maybeSingle()

      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '완료 처리 실패' }
    }
  }

  // 내 문의 목록 조회
  async getMyInquiries(): Promise<{ success: boolean; data: SupportInquiry[]; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) {
        return { success: false, data: [], error: '로그인이 필요합니다.' }
      }


      const { data, error } = await this.supabase
        .from('support_inquires')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })


      if (error) {
        throw error
      }

      // 완료되지 않은 문의를 먼저, 그 안에서 최신순 정렬
      const sortedData = this.sortInquiriesByStatus(data || [])

      return { success: true, data: sortedData }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '문의 조회 실패' }
    }
  }

  // 업체등록/수정 + 택배 주소록 수정 요청 문의 목록 조회 (lead buyer 관리자 모드용)
  // RLS: lead buyer는 inquiry_type in ('new_vendor','vendor_edit','shipping_edit') 문의 전체 조회 가능
  async getNewVendorInquiries(): Promise<{ success: boolean; data: SupportInquiry[]; error?: string }> {
    try {
      const { data, error } = await this.supabase
        .from('support_inquires')
        .select('*')
        .in('inquiry_type', ['new_vendor', 'vendor_edit', 'shipping_edit'])
        .order('created_at', { ascending: false })

      if (error) throw error

      return { success: true, data: this.sortInquiriesByStatus(data || []) }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '업체등록/수정 요청 조회 실패' }
    }
  }

  // 업체등록 요청 payload 수정 (lead buyer가 좌측 인풋에서 수정한 내용 저장)
  async updateInquiryPayload(inquiryId: number, payload: SupportInquiryPayload): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase
        .from('support_inquires')
        .update({ inquiry_payload: payload, updated_at: new Date().toISOString() })
        .eq('id', inquiryId)

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '요청 내역 저장 실패' }
    }
  }

  // 업체등록 요청 접수 → lead buyer + superadmin에게 푸시 알림
  async notifyLeadBuyersNewVendorRequest(params: { requesterName: string; vendorName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'new_vendor_inquiry',
          title: '🏢 신규 업체 등록 요청',
          body: `${params.requesterName}님이 업체 등록을 요청했습니다.\n업체명: ${params.vendorName}`,
          data: {
            type: 'new_vendor_inquiry',
            inquiryId: String(params.inquiryId ?? ''),
            vendor_name: params.vendorName,
            requester_name: params.requesterName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 문의 접수 자체를 막지 않음
      logger.warn('업체등록 요청 알림 발송 실패', { error: e })
    }
  }

  // 기존업체 수정 요청 접수 → lead buyer + superadmin에게 푸시 알림
  async notifyLeadBuyersVendorEditRequest(params: { requesterName: string; vendorName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'vendor_edit_inquiry',
          title: '✏️ 기존업체 수정 요청',
          body: `${params.requesterName}님이 업체 정보 수정을 요청했습니다.\n업체명: ${params.vendorName}`,
          data: {
            type: 'vendor_edit_inquiry',
            inquiryId: String(params.inquiryId ?? ''),
            vendor_name: params.vendorName,
            requester_name: params.requesterName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 문의 접수 자체를 막지 않음
      logger.warn('기존업체 수정 요청 알림 발송 실패', { error: e })
    }
  }

  // 업체 등록 완료 → 요청자에게 푸시 알림
  async notifyVendorRegistered(params: { targetEmail: string; vendorName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'new_vendor_registered',
          targetEmail: params.targetEmail,
          title: '업체 등록 완료',
          body: `요청하신 업체(${params.vendorName}) 등록이 완료되었습니다.`,
          data: {
            type: 'new_vendor_registered',
            inquiryId: String(params.inquiryId ?? ''),
            vendor_name: params.vendorName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 등록 처리 자체를 막지 않음
      logger.warn('업체 등록 완료 알림 발송 실패', { error: e })
    }
  }

  // 업체 정보 수정 완료 → 요청자에게 푸시 알림
  async notifyVendorEdited(params: { targetEmail: string; vendorName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'vendor_edited',
          targetEmail: params.targetEmail,
          title: '업체 정보 수정 완료',
          body: `요청하신 업체(${params.vendorName}) 정보 수정이 완료되었습니다.`,
          data: {
            type: 'vendor_edited',
            inquiryId: String(params.inquiryId ?? ''),
            vendor_name: params.vendorName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 수정 처리 자체를 막지 않음
      logger.warn('업체 정보 수정 완료 알림 발송 실패', { error: e })
    }
  }

  // 택배 주소록 수정 요청 접수 → lead buyer + superadmin에게 푸시 알림
  async notifyLeadBuyersShippingEditRequest(params: { requesterName: string; companyName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'shipping_edit_inquiry',
          title: '📦 택배 주소록 수정 요청',
          body: `${params.requesterName}님이 택배 주소록 수정을 요청했습니다.\n업체명: ${params.companyName}`,
          data: {
            type: 'shipping_edit_inquiry',
            inquiryId: String(params.inquiryId ?? ''),
            company_name: params.companyName,
            requester_name: params.requesterName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 문의 접수 자체를 막지 않음
      logger.warn('택배 주소록 수정 요청 알림 발송 실패', { error: e })
    }
  }

  // 택배 주소록 수정 완료 → 요청자에게 푸시 알림
  async notifyShippingEdited(params: { targetEmail: string; companyName: string; inquiryId?: number }): Promise<void> {
    try {
      await this.supabase.functions.invoke('send_fcm_notification', {
        body: {
          type: 'shipping_edited',
          targetEmail: params.targetEmail,
          title: '택배 주소록 수정 완료',
          body: `요청하신 택배 주소록(${params.companyName}) 수정이 완료되었습니다.`,
          data: {
            type: 'shipping_edited',
            inquiryId: String(params.inquiryId ?? ''),
            company_name: params.companyName
          }
        }
      })
    } catch (e) {
      // 알림 실패는 수정 처리 자체를 막지 않음
      logger.warn('택배 주소록 수정 완료 알림 발송 실패', { error: e })
    }
  }

  // 모든 문의 목록 조회 (관리자용)
  async getAllInquiries(): Promise<{ success: boolean; data: SupportInquiry[]; error?: string }> {
    try {
      const data = await fetchAllPages<SupportInquiry>((from, to) => this.supabase
        .from('support_inquires')
        .select('*')
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to))

      // 완료되지 않은 문의를 먼저, 그 안에서 최신순 정렬
      const sortedData = this.sortInquiriesByStatus(data)

      return { success: true, data: sortedData }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '문의 조회 실패' }
    }
  }

  // 문의 상태 업데이트 (관리자용)
  async updateInquiryStatus(
    inquiryId: number, 
    status: 'open' | 'in_progress' | 'resolved' | 'closed',
    resolution_note?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, error: '로그인이 필요합니다.' }

      const { data: employee } = await this.supabase
        .from('employees')
        .select('name')
        .eq('email', user.email)
        .single()

      const updateData: { status: string; handled_by: string; updated_at: string; processed_at?: string; resolution_note?: string } = {
        status,
        handled_by: employee?.name || user.email || '',
        updated_at: new Date().toISOString()
      }

      // resolved나 closed 상태로 변경 시 처리 시간 기록
      if (status === 'resolved' || status === 'closed') {
        updateData.processed_at = new Date().toISOString()
      }

      // resolution_note가 있으면 추가
      if (resolution_note) {
        updateData.resolution_note = resolution_note
      }

      const { error } = await this.supabase
        .from('support_inquires')
        .update(updateData)
        .eq('id', inquiryId)

      if (error) throw error

      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '상태 업데이트 실패' }
    }
  }


  // 내가 요청한 발주 목록 조회 (수정/삭제 요청용)
  async getMyPurchaseRequests(startDate?: string, endDate?: string, requesterName?: string) {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, data: [], error: '로그인이 필요합니다.' }

      let targetName = requesterName
      if (!targetName) {
        const { data: employee } = await this.supabase
          .from('employees')
          .select('name')
          .eq('email', user.email)
          .single()

        if (!employee) {
          return { success: false, data: [], error: '사용자 정보를 찾을 수 없습니다.' }
        }
        targetName = employee.name
      }

      const data = await fetchAllPages<Record<string, unknown>>((from, to) => {
        let query = this.supabase
          .from('purchase_requests')
          .select('id,purchase_order_number,vendor_name,request_date,created_at,requester_name,middle_manager_status,final_manager_status,delivery_request_date,revised_delivery_request_date,purchase_request_items(id,line_number,item_name,specification,quantity,unit_price_value,amount_value)')
          .is('deleted_at', null)
          .eq('requester_name', targetName)

        if (startDate) {
          query = query.gte('created_at', `${startDate}T00:00:00`)
        }
        if (endDate) {
          query = query.lte('created_at', `${endDate}T23:59:59.999`)
        }

        return query
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to)
      })

      return { success: true, data }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '발주요청 조회 실패' }
    }
  }

  async getEmployeeNames(): Promise<{ success: boolean; data: { name: string }[]; currentUserName?: string; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, data: [], error: '로그인이 필요합니다.' }

      const { data: employees, error } = await this.supabase
        .from('employees')
        .select('name')
        .order('name')

      if (error) throw error

      const { data: currentEmployee } = await this.supabase
        .from('employees')
        .select('name')
        .eq('email', user.email)
        .single()

      return {
        success: true,
        data: employees || [],
        currentUserName: currentEmployee?.name
      }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '직원 목록 조회 실패' }
    }
  }

  // 실시간 구독 설정
  subscribeToInquiries(callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void) {
    // 싱글톤 채널: 여러 컴포넌트가 동시에 구독해도 채널은 1개만 유지
    this.inquirySubscribers.add(callback)

    if (!this.inquiriesChannel) {
      this.inquiriesChannel = this.supabase
        .channel('support_inquires_realtime_singleton')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'support_inquires'
          },
          (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
            for (const sub of this.inquirySubscribers) {
              try {
                sub(payload)
              } catch {
                // ignore subscriber errors
              }
            }
          }
        )
        .subscribe((status: string, err?: Error) => {
          // status/err intentionally ignored
        })
    }

    return {
      unsubscribe: () => {
        this.inquirySubscribers.delete(callback)
        if (this.inquirySubscribers.size === 0 && this.inquiriesChannel) {
          this.inquiriesChannel.unsubscribe()
          this.inquiriesChannel = null
        }
      }
    }
  }

  // 발주요청 품목 수정
  async updatePurchaseRequestItem(
    itemId: string,
    updates: {
      item_name?: string
      specification?: string
      quantity?: number
      unit_price_value?: number
      amount_value?: number
      remark?: string
    }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: { item_name?: string; specification?: string; quantity?: number; remark?: string; unit_price_value?: number; unit_price_currency?: string; amount_value?: number; amount_currency?: string } = {}

      if (updates.item_name !== undefined) updateData.item_name = updates.item_name
      if (updates.specification !== undefined) updateData.specification = updates.specification
      if (updates.quantity !== undefined) updateData.quantity = updates.quantity
      if (updates.remark !== undefined) updateData.remark = updates.remark
      if (updates.unit_price_value !== undefined) {
        updateData.unit_price_value = updates.unit_price_value
        updateData.unit_price_currency = 'KRW'
      }
      if (updates.amount_value !== undefined) {
        updateData.amount_value = updates.amount_value
        updateData.amount_currency = 'KRW'
      }
      
      const { error } = await this.supabase
        .from('purchase_request_items')
        .update(updateData)
        .eq('id', itemId)

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '품목 수정 실패' }
    }
  }

  // 발주요청 품목 삭제(보관): RPC(soft_delete_purchase_items)로 deleted_at 마킹.
  // RLS SELECT 정책이 deleted_at 쓰기를 막으므로 SECURITY DEFINER RPC로 우회 + 남은 품목 line_number 재정렬(트리거)
  async deletePurchaseRequestItem(itemId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase
        .rpc('soft_delete_purchase_items', { p_item_ids: [Number(itemId)] })

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '품목 삭제 실패' }
    }
  }

  // 발주요청 전체 삭제(보관): RPC(soft_delete_purchase_order)로 deleted_at 마킹.
  // cascade 트리거가 품목 보관 + 발주번호/수주번호 _D 처리. RLS 우회 위해 SECURITY DEFINER RPC 사용.
  async deletePurchaseRequest(requestId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase
        .rpc('soft_delete_purchase_order', { p_id: Number(requestId) })

      if (error) {
        logger.error('[deletePurchaseRequest] 발주요청 보관 처리 실패', error, {
          requestId,
          error
        })
        throw error
      }

      logger.debug('[deletePurchaseRequest] 발주요청 보관(soft delete) 성공', { requestId })
      return { success: true }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : '발주요청 삭제 실패'
      logger.error('[deletePurchaseRequest] 보관 처리 실패', e instanceof Error ? e : undefined, {
        requestId,
        error: errorMessage
      })
      return { success: false, error: errorMessage }
    }
  }

  // 발주요청 상세 조회
  async getPurchaseRequestDetail(requestId: string): Promise<{ success: boolean; data?: Record<string, unknown>; error?: string }> {
    try {
      const { data, error } = await this.supabase
        .from('purchase_requests')
        .select('*,purchase_request_items(id,line_number,item_name,specification,quantity,unit_price_value,amount_value,remark,link)')
        .eq('id', requestId)
        .single()

      if (error) throw error

      return { success: true, data }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '발주요청 조회 실패' }
    }
  }

  // 문의 삭제
  async deleteInquiry(inquiryId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user }, error: authError } = await this.supabase.auth.getUser()
      if (authError || !user) return { success: false, error: '로그인이 필요합니다.' }

      // 관리자 권한 확인
      const { data: employee } = await this.supabase
        .from('employees')
        .select('roles')
        .eq('email', user.email)
        .single()

      const roles = parseRoles(employee?.roles)
      const isAdmin = roles.includes('superadmin')
      const isLeadBuyer = roles.includes('lead buyer')

      // 관리자가 아닌 경우에만 본인 문의 확인
      if (!isAdmin) {
        // 문의 정보 확인 (본인 것인지)
        const { data: inquiry, error: fetchError } = await this.supabase
          .from('support_inquires')
          .select('user_id, status, resolution_note, inquiry_type')
          .eq('id', inquiryId)
          .single()

        if (fetchError || !inquiry) {
          return { success: false, error: '문의를 찾을 수 없습니다.' }
        }

        // lead buyer는 업체등록 요청 건을 상태 무관하게 삭제 가능 (관리자 모드)
        if (isLeadBuyer && inquiry.inquiry_type === 'new_vendor') {
          const { error: lbDeleteError } = await this.supabase
            .from('support_inquires')
            .delete()
            .eq('id', inquiryId)
          if (lbDeleteError) throw lbDeleteError
          return { success: true }
        }

        // 본인 문의가 아니면 삭제 불가
        if (inquiry.user_id !== user.id) {
          return { success: false, error: '본인의 문의만 삭제할 수 있습니다.' }
        }

        // 일반 사용자는 답변이 있거나 처리중인 문의는 삭제 불가
        if (inquiry.resolution_note) {
          return { success: false, error: '답변이 완료된 문의는 삭제할 수 없습니다.' }
        }

        if (inquiry.status !== 'open') {
          return { success: false, error: '처리가 진행된 문의는 삭제할 수 없습니다.' }
        }
      }
      // 관리자는 모든 문의 삭제 가능 (제한 없음)

      // 삭제 실행
      const { error } = await this.supabase
        .from('support_inquires')
        .delete()
        .eq('id', inquiryId)

      if (error) throw error
      return { success: true }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '문의 삭제 실패' }
    }
  }

  // 거래명세서 목록 조회 (삭제 요청 폼용)
  async getStatementsForSupport(dateFrom?: string, dateTo?: string): Promise<{
    success: boolean
    data: SupportStatementRow[]
    error?: string
  }> {
    try {
      const data = await fetchAllPages<SupportStatementRow>((from, to) => {
        let query = this.supabase
          .from('transaction_statements')
          .select('id,statement_code,vendor_name,statement_date,uploaded_at,file_name,total_amount,grand_total,status')

        if (dateFrom) {
          query = query.gte('uploaded_at', dateFrom)
        }
        if (dateTo) {
          query = query.lte('uploaded_at', dateTo + 'T23:59:59')
        }

        return query
          .order('uploaded_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to)
      })

      return { success: true, data }
    } catch (e) {
      return { success: false, data: [], error: e instanceof Error ? e.message : '거래명세서 조회 실패' }
    }
  }
}

export const supportService = new SupportService()